import React, { useEffect } from 'react';

const DpaView: React.FC = () => {
  useEffect(() => {
    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';
    const root = document.getElementById('root');
    if (root) root.style.overflow = 'auto';
    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      if (root) root.style.overflow = '';
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0a1628] text-[#c8daf5] py-16 px-4">
      <div className="max-w-3xl mx-auto">

        {/* Header */}
        <div className="mb-12 text-center">
          <img src="/images/logo-orion.png" alt="ORION CRM" className="h-10 w-auto mx-auto mb-6 opacity-80" />
          <h1 className="text-3xl font-black text-white mb-3">Accord de traitement des données</h1>
          <p className="text-sm text-[#546a96]">Data Processing Agreement (DPA) — Version 1.0 — En vigueur depuis le 4 avril 2026</p>
          <div className="mt-6 h-px bg-gradient-to-r from-transparent via-[rgba(100,140,210,0.3)] to-transparent" />
        </div>

        <div className="space-y-10 text-sm leading-relaxed">

          {/* Préambule */}
          <section>
            <h2 className="text-base font-black text-white mb-3 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[rgba(17,197,214,0.15)] text-[#11c5d6] text-[10px] font-black flex items-center justify-center flex-shrink-0">P</span>
              Préambule
            </h2>
            <div className="pl-8 text-[#7f93bc] space-y-2">
              <p>
                Le présent Accord de traitement des données (ci-après « DPA ») est conclu conformément à l'article 28 du Règlement (UE) 2016/679 du Parlement européen et du Conseil du 27 avril 2016 (RGPD).
              </p>
              <p>
                Il régit les conditions dans lesquelles <strong className="text-[#c8daf5]">Orion CRM</strong> (ci-après le « Sous-traitant ») traite des données personnelles pour le compte du client (ci-après le « Responsable du traitement ») dans le cadre de l'utilisation du logiciel Orion CRM.
              </p>
              <p>
                En acceptant cet accord lors de la création de votre compte, le Responsable du traitement et le Sous-traitant s'engagent à respecter les obligations définies ci-après.
              </p>
            </div>
          </section>

          {/* 1. Objet */}
          <section>
            <h2 className="text-base font-black text-white mb-3 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[rgba(17,197,214,0.15)] text-[#11c5d6] text-[10px] font-black flex items-center justify-center flex-shrink-0">1</span>
              Objet et durée
            </h2>
            <div className="pl-8 text-[#7f93bc] space-y-2">
              <p>Le Sous-traitant s'engage à traiter les données personnelles uniquement dans le cadre de la fourniture du service Orion CRM, à savoir :</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Hébergement et stockage des données CRM</li>
                <li>Gestion des utilisateurs et des accès</li>
                <li>Traitement des données prospects (leads)</li>
                <li>Envoi de communications transactionnelles (réinitialisation de mot de passe, notifications)</li>
              </ul>
              <p className="mt-2">Le présent accord est conclu pour la durée du contrat de service entre les parties. Il prend fin automatiquement à la résiliation ou à l'expiration dudit contrat.</p>
            </div>
          </section>

          {/* 2. Nature des données */}
          <section>
            <h2 className="text-base font-black text-white mb-3 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[rgba(17,197,214,0.15)] text-[#11c5d6] text-[10px] font-black flex items-center justify-center flex-shrink-0">2</span>
              Nature des données traitées
            </h2>
            <div className="pl-8 text-[#7f93bc]">
              <div className="overflow-x-auto">
                <table className="w-full text-xs mt-2 border-collapse">
                  <thead>
                    <tr className="border-b border-[rgba(100,140,210,0.2)]">
                      <th className="text-left py-2 pr-4 text-[#11c5d6] font-bold">Catégorie</th>
                      <th className="text-left py-2 pr-4 text-[#11c5d6] font-bold">Données concernées</th>
                      <th className="text-left py-2 text-[#11c5d6] font-bold">Personnes concernées</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[rgba(100,140,210,0.1)]">
                    <tr>
                      <td className="py-2 pr-4 font-medium text-[#c8daf5]">Données d'identification</td>
                      <td className="py-2 pr-4">Nom, prénom, adresse email</td>
                      <td className="py-2">Utilisateurs et prospects</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4 font-medium text-[#c8daf5]">Données de contact</td>
                      <td className="py-2 pr-4">Numéro de téléphone (fixe et mobile)</td>
                      <td className="py-2">Prospects</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4 font-medium text-[#c8daf5]">Données professionnelles</td>
                      <td className="py-2 pr-4">Nom d'entreprise, secteur d'activité, CA potentiel</td>
                      <td className="py-2">Prospects</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4 font-medium text-[#c8daf5]">Données de connexion</td>
                      <td className="py-2 pr-4">Adresse IP, horodatage de connexion, actions effectuées</td>
                      <td className="py-2">Utilisateurs</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* 3. Obligations du sous-traitant */}
          <section>
            <h2 className="text-base font-black text-white mb-3 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[rgba(17,197,214,0.15)] text-[#11c5d6] text-[10px] font-black flex items-center justify-center flex-shrink-0">3</span>
              Obligations du Sous-traitant (Orion CRM)
            </h2>
            <div className="pl-8 text-[#7f93bc] space-y-2">
              <p>Le Sous-traitant s'engage à :</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Ne traiter les données qu'sur instruction documentée du Responsable du traitement</li>
                <li>Garantir la confidentialité des données (accès restreint aux personnels autorisés)</li>
                <li>Mettre en œuvre les mesures de sécurité techniques appropriées : chiffrement AES-256-GCM, hachage bcrypt des mots de passe, HTTPS, tokens JWT à durée limitée</li>
                <li>Ne pas recruter de sous-traitants ultérieurs sans accord préalable du Responsable</li>
                <li>Assister le Responsable dans l'exercice des droits des personnes concernées (accès, rectification, effacement, portabilité)</li>
                <li>Notifier toute violation de données dans les 72 heures suivant sa découverte</li>
                <li>Supprimer ou restituer toutes les données à l'issue du contrat</li>
                <li>Mettre à disposition les informations nécessaires pour démontrer la conformité et permettre les audits</li>
              </ul>
            </div>
          </section>

          {/* 4. Obligations du responsable */}
          <section>
            <h2 className="text-base font-black text-white mb-3 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[rgba(17,197,214,0.15)] text-[#11c5d6] text-[10px] font-black flex items-center justify-center flex-shrink-0">4</span>
              Obligations du Responsable du traitement
            </h2>
            <div className="pl-8 text-[#7f93bc] space-y-2">
              <p>Le Responsable du traitement s'engage à :</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Ne collecter et ne saisir dans Orion CRM que des données pour lesquelles il dispose d'une base légale valide (consentement, intérêt légitime, contrat)</li>
                <li>Informer les personnes concernées de l'utilisation de leurs données</li>
                <li>Gérer les demandes d'exercice de droits des personnes concernées</li>
                <li>Ne pas utiliser Orion CRM pour traiter des données sensibles (catégories particulières au sens de l'art. 9 RGPD)</li>
                <li>Maintenir un registre des activités de traitement incluant le recours à Orion CRM</li>
              </ul>
            </div>
          </section>

          {/* 5. Sous-traitants ultérieurs */}
          <section>
            <h2 className="text-base font-black text-white mb-3 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[rgba(17,197,214,0.15)] text-[#11c5d6] text-[10px] font-black flex items-center justify-center flex-shrink-0">5</span>
              Sous-traitants ultérieurs
            </h2>
            <div className="pl-8 text-[#7f93bc]">
              <p className="mb-3">Le Sous-traitant peut faire appel aux sous-traitants ultérieurs suivants :</p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="border-b border-[rgba(100,140,210,0.2)]">
                      <th className="text-left py-2 pr-4 text-[#11c5d6] font-bold">Prestataire</th>
                      <th className="text-left py-2 pr-4 text-[#11c5d6] font-bold">Rôle</th>
                      <th className="text-left py-2 text-[#11c5d6] font-bold">Localisation</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[rgba(100,140,210,0.1)]">
                    <tr>
                      <td className="py-2 pr-4 font-medium text-[#c8daf5]">Hébergeur web</td>
                      <td className="py-2 pr-4">Hébergement des serveurs et de la base de données</td>
                      <td className="py-2">UE (France)</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4 font-medium text-[#c8daf5]">Prestataire SMTP</td>
                      <td className="py-2 pr-4">Envoi des emails transactionnels (configurable par le client)</td>
                      <td className="py-2">Variable (configurable)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="mt-3 text-xs text-[#546a96]">Tout ajout de sous-traitant ultérieur fera l'objet d'une notification préalable au Responsable du traitement.</p>
            </div>
          </section>

          {/* 6. Sécurité */}
          <section>
            <h2 className="text-base font-black text-white mb-3 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[rgba(17,197,214,0.15)] text-[#11c5d6] text-[10px] font-black flex items-center justify-center flex-shrink-0">6</span>
              Mesures de sécurité techniques et organisationnelles
            </h2>
            <div className="pl-8 text-[#7f93bc] space-y-1">
              <ul className="list-disc pl-5 space-y-1">
                <li>Chiffrement des données PII au repos : AES-256-GCM</li>
                <li>Chiffrement des mots de passe : bcrypt (coût 12)</li>
                <li>Authentification : JWT access token (1h) + refresh token HttpOnly (30j)</li>
                <li>Protection contre la force brute : rate-limiting par IP et par compte</li>
                <li>Contrôle d'accès basé sur les rôles (RBAC)</li>
                <li>Journalisation de toutes les actions sensibles (audit log)</li>
                <li>Rétention automatique des logs : 12 mois maximum</li>
                <li>Transport chiffré : HTTPS/TLS obligatoire en production</li>
              </ul>
            </div>
          </section>

          {/* 7. Transferts hors UE */}
          <section>
            <h2 className="text-base font-black text-white mb-3 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[rgba(17,197,214,0.15)] text-[#11c5d6] text-[10px] font-black flex items-center justify-center flex-shrink-0">7</span>
              Transferts de données hors Union Européenne
            </h2>
            <div className="pl-8 text-[#7f93bc]">
              <p>Orion CRM héberge l'ensemble des données au sein de l'Union Européenne. Aucun transfert de données vers des pays tiers n'est effectué sans garanties appropriées au sens des articles 44 à 49 du RGPD.</p>
            </div>
          </section>

          {/* 8. Sort des données */}
          <section>
            <h2 className="text-base font-black text-white mb-3 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[rgba(17,197,214,0.15)] text-[#11c5d6] text-[10px] font-black flex items-center justify-center flex-shrink-0">8</span>
              Sort des données en fin de contrat
            </h2>
            <div className="pl-8 text-[#7f93bc] space-y-2">
              <p>À l'issue du contrat, le Sous-traitant s'engage à :</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Permettre l'export complet des données du Responsable (format JSON) pendant une période de 30 jours suivant la résiliation</li>
                <li>Procéder à la suppression définitive et sécurisée de toutes les données à l'expiration de ce délai</li>
                <li>Fournir une attestation de suppression sur demande</li>
              </ul>
            </div>
          </section>

          {/* 9. Contact */}
          <section>
            <h2 className="text-base font-black text-white mb-3 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[rgba(17,197,214,0.15)] text-[#11c5d6] text-[10px] font-black flex items-center justify-center flex-shrink-0">9</span>
              Contact et droit applicable
            </h2>
            <div className="pl-8 text-[#7f93bc] space-y-2">
              <p>Pour toute question relative au présent accord :</p>
              <p><a href="mailto:dpa@crm-orion.com" className="text-[#11c5d6] hover:underline">dpa@crm-orion.com</a></p>
              <p className="mt-2">Le présent accord est régi par le droit français. Tout litige relatif à son interprétation ou à son exécution sera soumis aux juridictions compétentes du ressort de Paris.</p>
            </div>
          </section>

        </div>

        {/* Footer */}
        <div className="mt-16 pt-8 border-t border-[rgba(100,140,210,0.2)] text-center space-y-3">
          <p className="text-xs text-[#546a96]">DPA v1.0 — Conforme au RGPD art. 28 — Orion CRM © 2026</p>
          <a href="/politique-de-confidentialite" target="_blank" rel="noopener noreferrer" className="text-xs text-[#546a96] hover:text-[#11c5d6] transition-colors block">
            Politique de confidentialité
          </a>
          <a href="/" className="text-xs text-[#546a96] hover:text-[#11c5d6] transition-colors block">
            ← Retour à la connexion
          </a>
        </div>

      </div>
    </div>
  );
};

export default DpaView;
